<template>
  <PaginaAdministrar
    titulo="Operações de Financiamento"
    subtitulo="Forfait, Capital de Giro e outros"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    label-botao-adicionar="Nova Operação"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Tipo de Financiamento</label>
          <select class="form-select" v-model="filtros.lstTipoFinanciamento" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="t in listas.tiposFinanciamento" :key="t.idListaClienteItem" :value="t.idListaClienteItem">
              {{ t.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaClienteItem" :value="s.idListaClienteItem">
              {{ s.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-4 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma operação de financiamento encontrada."
      row-key="idOperacaoFinanciamento"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarRegistro($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idOperacaoFinanciamento }}</span>
      </template>

      <template #instituicao="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.nomeInstituicaoFinanceira }}</span>
      </template>

      <template #tipo="{ row }">
        <span class="badge bg-body-tertiary text-body border border-secondary-subtle">
          {{ getTipoTexto(row.lstTipoFinanciamento) }}
        </span>
      </template>

      <template #condicao="{ row }">
        <span class="small text-muted">{{ row.descricaoCondicaoPagamento || '-' }}</span>
      </template>

      <template #captacao="{ row }">
        <span v-if="row.idTituloCaptacao" class="badge bg-body-secondary text-body border border-secondary-subtle">
          #{{ row.idTituloCaptacao }}
        </span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #iof="{ row }">
        <span v-if="row.valorIof > 0" class="small">{{ formatarValor(row.valorIof) }}</span>
        <span v-else class="text-muted">-</span>
      </template>

      <template #valor="{ row }">
        <span class="fw-medium">{{ formatarValor(row.valorFinanciado) }}</span>
      </template>

      <template #moeda="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ getMoedaIso(row.lstMoeda) }}
        </span>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterCorStatus(row.lstStatusFinanciamento)">
          {{ getStatusTexto(row.lstStatusFinanciamento) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon" @click="editarRegistro(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="OperacaoFinanciamento"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import { FIN_FINANCIAMENTO_ST } from '@/constants/FIN_FINANCIAMENTO_ST'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  tiposFinanciamento: [],
  status: [],
  moedas: []
})

const filtros = reactive({
  lstTipoFinanciamento: '',
  lstStatus: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.tiposFinanciamento = []
    listas.status = []
    listas.moedas = []
  }
})

const colunas = [
  { field: 'idOperacaoFinanciamento', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'nomeInstituicaoFinanceira', label: 'Instituição', sortable: true, slotName: 'instituicao' },
  { field: 'lstTipoFinanciamento', label: 'Tipo', align: 'center', sortable: true, slotName: 'tipo' },
  { field: 'descricaoCondicaoPagamento', label: 'Condição', align: 'center', slotName: 'condicao' },
  { field: 'idTituloCaptacao', label: 'Captação', align: 'center', slotName: 'captacao' },
  { field: 'valorIof', label: 'IOF', align: 'right', slotName: 'iof' },
  { field: 'valorFinanciado', label: 'Valor Financiado', align: 'right', sortable: true, slotName: 'valor' },
  { field: 'lstMoeda', label: 'Moeda', align: 'center', slotName: 'moeda', sortable: true },
  { field: 'lstStatusFinanciamento', label: 'Status', align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 100, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'nomeInstituicaoFinanceira') {
    return [...data].sort((a, b) => {
      const textoA = (a.nomeInstituicaoFinanceira || '').toLowerCase()
      const textoB = (b.nomeInstituicaoFinanceira || '').toLowerCase()
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'lstTipoFinanciamento') {
    return [...data].sort((a, b) => {
      const textoA = getTipoTexto(a.lstTipoFinanciamento)
      const textoB = getTipoTexto(b.lstTipoFinanciamento)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'lstStatusFinanciamento') {
    return [...data].sort((a, b) => {
      const textoA = getStatusTexto(a.lstStatusFinanciamento)
      const textoB = getStatusTexto(b.lstStatusFinanciamento)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'lstMoeda') {
    return [...data].sort((a, b) => {
      const textoA = getMoedaIso(a.lstMoeda)
      const textoB = getMoedaIso(b.lstMoeda)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const handleSortChange = () => { buscarDados(1) }

const getTipoTexto = (idTipo) => {
  if (!idTipo) return '-'
  const idStr = String(idTipo)
  const item = listas.tiposFinanciamento.find(s => String(s.idListaClienteItem) === idStr)
  return item ? item.textoLista : idTipo
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const idStr = String(idStatus)
  const item = listas.status.find(s => String(s.idListaClienteItem) === idStr)
  return item ? item.textoLista : idStatus
}

const getMoedaIso = (idMoeda) => {
  if (!idMoeda) return 'BRL'
  const idStr = String(idMoeda)
  const moeda = listas.moedas.find(m => String(m.idListaClienteItem) === idStr)
  const texto = moeda ? moeda.textoLista : idMoeda
  const match = texto.match(/^[A-Z]{3}/)
  return match ? match[0] : 'BRL'
}

const obterCorStatus = (lstStatus) => {
  if (!lstStatus) return 'badge-ghost-info'
  if (lstStatus === FIN_FINANCIAMENTO_ST.Cancelado) return 'badge-ghost-danger'
  if (lstStatus === FIN_FINANCIAMENTO_ST.Liquidado) return 'badge-ghost-success'
  if (lstStatus === FIN_FINANCIAMENTO_ST.Contratado || lstStatus === FIN_FINANCIAMENTO_ST.EmAmortizacao) return 'badge-ghost-primary'
  if (lstStatus === FIN_FINANCIAMENTO_ST.EmDigitacao) return 'badge-ghost-warning'
  return 'badge-ghost-info'
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['OperacaoFinanciamento'])
    listas.tiposFinanciamento = result.LstTipoFinanciamento || []
    listas.status = result.LstStatusFinanciamento || []
    listas.moedas = result.LstMoeda || []
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.lstTipoFinanciamento) params.append('lstTipoFinanciamento', filtros.lstTipoFinanciamento)
    if (filtros.lstStatus) params.append('lstStatusFinanciamento', filtros.lstStatus)

    const response = await api.get(`/OperacoesFinanciamentos?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.lstTipoFinanciamento = ''; filtros.lstStatus = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarRegistro = async (item) => {
  try {
    loading.value = true
    const response = await api.get(`/OperacoesFinanciamentos/${item.idOperacaoFinanciamento}`)
    emit('editar-item', response.data)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados do financiamento.', type: 'error' })
  } finally {
    loading.value = false
  }
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idOperacaoFinanciamento
  auditoriaSelecionada.nome = `Financiamento #${item.idOperacaoFinanciamento}`
  mostrarAuditoria.value = true
}

onMounted(async () => {
  if (authStore.empresaAtiva || !authStore.isAdminInterno) {
    await carregarListas()
    buscarDados(1)
  }
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

defineExpose({ buscarDados })
</script>
